import React from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { AppBar, Toolbar, Typography, Button, Box, Chip, IconButton, Tooltip } from '@mui/material';
import SportsBasketballIcon from '@mui/icons-material/SportsBasketball';
import LogoutIcon from '@mui/icons-material/Logout';
import { useAuth } from '../context/AuthContext';

const Navbar = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, subscription, logout } = useAuth();

  const navItems = [
    { path: '/dashboard', label: 'Dashboard', icon: '📊' },
    { path: '/players', label: 'Oyuncular', icon: '🏀' },
    { path: '/teams', label: 'Takımlar', icon: '👥' },
    { path: '/predictions', label: 'Tahminler', icon: '🎯' },
  ];

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  // Calculate days left for trial
  const getDaysLeft = () => {
    if (subscription?.type === 'premium') return null;
    return subscription?.days_left || 0;
  };

  const daysLeft = getDaysLeft();

  return (
    <AppBar position="static" sx={{ background: 'transparent', boxShadow: 'none', borderBottom: '1px solid rgba(255,255,255,0.1)' }}>
      <Toolbar sx={{ 
        maxWidth: 1400, 
        width: '100%', 
        mx: 'auto', 
        px: { xs: 1, md: 2 },
        minHeight: { xs: 56, md: 64 }
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexGrow: 1 }}>
          <SportsBasketballIcon sx={{ color: '#ff6b35', fontSize: { xs: 24, md: 28 } }} />
          <Box sx={{ display: 'flex', flexDirection: 'column', lineHeight: 1 }}>
            <Typography variant="h6" sx={{ fontWeight: 700, fontSize: { xs: 14, md: 18 }, lineHeight: 1 }}>
              <span style={{ color: '#fff' }}>NBA</span>
              <span style={{ color: '#ff6b35' }}>Analytics</span>
            </Typography>
            <Typography sx={{ fontSize: { xs: 8, md: 10 }, color: '#6b7280', mt: 0.2 }}>
              by basketanaliz.com
            </Typography>
          </Box>
          <Chip 
            label="2025-26" 
            size="small" 
            sx={{ 
              ml: 0.5, 
              bgcolor: 'rgba(255,255,255,0.1)', 
              color: '#9ca3af',
              fontSize: { xs: 9, md: 11 },
              height: { xs: 20, md: 24 },
              display: { xs: 'none', sm: 'flex' }
            }} 
          />
        </Box>
        
        <Box sx={{ display: 'flex', gap: { xs: 0, md: 0.5 }, alignItems: 'center' }}>
          {navItems.map((item) => (
            <Button
              key={item.path}
              component={Link}
              to={item.path}
              sx={{
                color: location.pathname === item.path ? '#ff6b35' : '#9ca3af',
                fontSize: { xs: 10, md: 13 },
                fontWeight: 500,
                px: { xs: 1, md: 2 },
                py: 1,
                minWidth: { xs: 'auto', md: 64 },
                position: 'relative',
                textTransform: 'none',
                '&:hover': { color: '#ff6b35', bgcolor: 'transparent' },
                '&::after': location.pathname === item.path ? {
                  content: '""',
                  position: 'absolute',
                  bottom: 0,
                  left: '50%',
                  transform: 'translateX(-50%)',
                  width: { xs: 16, md: 24 },
                  height: 2,
                  bgcolor: '#ff6b35',
                  borderRadius: 1
                } : {}
              }}
            >
              <Box component="span" sx={{ mr: { xs: 0, sm: 0.5 }, fontSize: { xs: 14, md: 16 } }}>{item.icon}</Box>
              <Box component="span" sx={{ display: { xs: 'none', sm: 'inline' } }}>{item.label}</Box>
            </Button>
          ))}

          {/* Subscription Status */}
          {daysLeft !== null && (
            <>
              <Chip
                label={daysLeft > 0 ? `${daysLeft} gün kaldı` : 'Deneme bitti'}
                size="small"
                sx={{
                  ml: 1,
                  bgcolor: daysLeft > 3 ? 'rgba(0, 217, 192, 0.1)' : 'rgba(239, 68, 68, 0.1)',
                  color: daysLeft > 3 ? '#00d9c0' : '#ef4444',
                  border: `1px solid ${daysLeft > 3 ? 'rgba(0, 217, 192, 0.3)' : 'rgba(239, 68, 68, 0.3)'}`,
                  fontSize: { xs: 9, md: 11 },
                  height: { xs: 22, md: 26 },
                  display: { xs: 'none', md: 'flex' }
                }}
              />
              <Button
                component={Link}
                to="/payment"
                size="small"
                sx={{
                  ml: 1,
                  background: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)',
                  color: '#000',
                  fontSize: { xs: 9, md: 11 },
                  fontWeight: 600,
                  px: { xs: 1, md: 1.5 },
                  py: 0.5,
                  borderRadius: '6px',
                  textTransform: 'none',
                  '&:hover': { 
                    background: 'linear-gradient(135deg, #d97706 0%, #b45309 100%)',
                  }
                }}
              >
                ⭐ Premium
              </Button>
            </>
          )}

          {subscription?.type === 'premium' && (
            <Chip
              label="⭐ Premium"
              size="small"
              sx={{
                ml: 1,
                bgcolor: 'rgba(255, 107, 74, 0.1)',
                color: '#ff6b4a',
                border: '1px solid rgba(255, 107, 74, 0.3)',
                fontSize: { xs: 9, md: 11 },
                height: { xs: 22, md: 26 },
                display: { xs: 'none', md: 'flex' }
              }}
            />
          )}

          {/* Admin Link */}
          {user?.is_admin && (
            <Button
              component={Link}
              to="/admin"
              sx={{
                ml: 1,
                color: '#00d9c0',
                fontSize: { xs: 10, md: 12 },
                fontWeight: 600,
                px: 1.5,
                py: 0.5,
                bgcolor: 'rgba(0, 217, 192, 0.1)',
                border: '1px solid rgba(0, 217, 192, 0.3)',
                borderRadius: '6px',
                textTransform: 'none',
                '&:hover': { bgcolor: 'rgba(0, 217, 192, 0.2)' }
              }}
            >
              👑 Admin
            </Button>
          )}

          {/* Logout Button */}
          <Tooltip title={user?.email || 'Çıkış Yap'}>
            <IconButton
              onClick={handleLogout}
              sx={{
                ml: 1,
                color: '#9ca3af',
                '&:hover': { color: '#ff6b35', bgcolor: 'rgba(255, 107, 53, 0.1)' }
              }}
            >
              <LogoutIcon sx={{ fontSize: { xs: 18, md: 22 } }} />
            </IconButton>
          </Tooltip>
        </Box>
      </Toolbar>
    </AppBar>
  );
};

export default Navbar;
